/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Eye, Key, Pencil, Plus, Trash2, Users } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../services/apiService";
import { Badge } from "../../../components/ops/Badge";
import { useConfirm } from "../../../components/ops/ConfirmProvider";
import { DataTable } from "../../../components/ops/DataTable";
import { DataToolbar } from "../../../components/ops/DataToolbar";
import { EmptyState } from "../../../components/ops/EmptyState";
import { Modal } from "../../../components/ops/Modal";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Pagination } from "../../../components/ops/Pagination";
import { useToast } from "../../../components/ops/ToastProvider";
import { usePagedList } from "../../../components/ops/usePagedList";

const roles = ["parent", "coach", "staff", "admin"];
const roleTone = { admin: "accent", superadmin: "accent", staff: "success", coach: "success", parent: "neutral" };
const emptyCreateForm = { firstName: "", lastName: "", email: "", password: "", role: "parent" };
const emptyEditForm = { firstName: "", lastName: "", email: "" };

export function AdminUsersPage() {
  const { user, accessToken } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [verifiedFilter, setVerifiedFilter] = useState("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [creating, setCreating] = useState(false);

  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [savingEdit, setSavingEdit] = useState(false);

  const [passwordUser, setPasswordUser] = useState(null);
  const [passwordValue, setPasswordValue] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const [viewingUser, setViewingUser] = useState(null);
  const [viewDetail, setViewDetail] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (verifiedFilter !== "all" && (verifiedFilter === "verified") !== Boolean(u.verifiedAt)) return false;
      return true;
    });
  }, [users, roleFilter, verifiedFilter]);

  const { page, setPage, totalPages, totalItems, pageItems } = usePagedList(filteredUsers, {
    search,
    searchFields: ["firstName", "lastName", "email"],
    pageSize: 10,
  });

  const load = () => {
    setLoading(true);
    api.get("/admin/users")
      .then((response) => setUsers(response.data.users || []))
      .catch(() => toast.error("Unable to load users"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [accessToken]);

  const changeRole = async (targetUser, role) => {
    const ok = await confirm({
      title: "Change this user's role?",
      message: `${targetUser.firstName} ${targetUser.lastName} will become "${role}". This changes what they can access immediately.`,
      confirmLabel: "Change role",
      tone: "danger",
    });
    if (!ok) return;

    try {
      const response = await api.patch(`/admin/users/${targetUser._id}/role`, { role });
      setUsers(users.map((u) => (u._id === response.data.user._id ? response.data.user : u)));
      toast.success(`${targetUser.firstName}'s role is now ${role}.`);
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to update role");
    }
  };

  const openCreate = () => {
    setCreateForm(emptyCreateForm);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateForm(emptyCreateForm);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await api.post("/admin/users", createForm);
      setUsers([response.data.user, ...users]);
      toast.success("User created successfully!");
      closeCreate();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to create user");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (targetUser) => {
    setEditingUser(targetUser);
    setEditForm({ firstName: targetUser.firstName, lastName: targetUser.lastName, email: targetUser.email });
  };

  const closeEdit = () => {
    setEditingUser(null);
    setEditForm(emptyEditForm);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const response = await api.patch(`/admin/users/${editingUser._id}`, editForm);
      setUsers(users.map((u) => (u._id === response.data.user._id ? response.data.user : u)));
      toast.success("User updated successfully!");
      closeEdit();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to update user");
    } finally {
      setSavingEdit(false);
    }
  };

  const openPassword = (targetUser) => {
    setPasswordUser(targetUser);
    setPasswordValue("");
  };

  const closePassword = () => {
    setPasswordUser(null);
    setPasswordValue("");
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setSavingPassword(true);
    try {
      await api.patch(`/admin/users/${passwordUser._id}/password`, { password: passwordValue });
      toast.success(`Password updated for ${passwordUser.firstName}.`);
      closePassword();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to update password");
    } finally {
      setSavingPassword(false);
    }
  };

  const openView = async (targetUser) => {
    setViewingUser(targetUser);
    setViewDetail(null);
    setViewLoading(true);
    try {
      const response = await api.get(`/admin/users/${targetUser._id}`);
      setViewDetail(response.data);
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to load user details");
    } finally {
      setViewLoading(false);
    }
  };

  const removeUser = async (targetUser) => {
    const ok = await confirm({
      title: "Delete this user?",
      message: `"${targetUser.firstName} ${targetUser.lastName}" will be permanently removed. This can't be undone.`,
      confirmLabel: "Delete user",
      tone: "danger",
    });
    if (!ok) return;

    try {
      await api.delete(`/admin/users/${targetUser._id}`);
      setUsers(users.filter((u) => u._id !== targetUser._id));
      toast.success("User deleted.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to delete user");
    }
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      render: (row) => (
        <div className="ops-table-player">
          <span className="ops-table-avatar">{row.firstName?.charAt(0)}{row.lastName?.charAt(0)}</span>
          {row.firstName} {row.lastName}
        </div>
      ),
    },
    { key: "email", label: "Email", render: (row) => row.email },
    { key: "verified", label: "Verified", render: (row) => (row.verifiedAt ? <Badge tone="success">Verified</Badge> : <Badge tone="neutral">Unverified</Badge>) },
    {
      key: "role",
      label: "Role",
      render: (row) => {
        if (row._id === user?.id || row.role === "superadmin") return <Badge tone={roleTone[row.role] || "neutral"}>{row.role}</Badge>;
        return (
          <select className="ops-select" style={{ width: "auto", padding: "6px 10px" }} value={row.role} onChange={(e) => changeRole(row, e.target.value)}>
            {roles.map((role) => <option key={role} value={role}>{role}</option>)}
          </select>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => {
        const isSelf = row._id === user?.id;
        const isSuperadmin = row.role === "superadmin";
        return (
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button type="button" className="ops-button ops-button--outline ops-button--small" onClick={() => openView(row)} aria-label="View user">
              <Eye size={13} />
            </button>
            <button type="button" className="ops-button ops-button--outline ops-button--small" onClick={() => openEdit(row)} aria-label="Edit user">
              <Pencil size={13} />
            </button>
            <button type="button" className="ops-button ops-button--outline ops-button--small" onClick={() => openPassword(row)} aria-label="Set password">
              <Key size={13} />
            </button>
            <button
              type="button"
              className="ops-button ops-button--danger ops-button--small"
              onClick={() => removeUser(row)}
              aria-label="Delete user"
              disabled={isSelf || isSuperadmin}
              title={isSelf ? "You cannot delete your own account" : isSuperadmin ? "Super admin accounts cannot be deleted" : "Delete user"}
            >
              <Trash2 size={13} />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Manage accounts and roles across the platform"
        actions={
          <button type="button" className="ops-button ops-button--primary" onClick={openCreate}>
            <Plus size={16} /> New User
          </button>
        }
      />

      <div className="ops-card">
        <DataToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name or email..."
          filters={[
            {
              label: "Role",
              value: roleFilter,
              onChange: setRoleFilter,
              options: [{ value: "all", label: "All roles" }, ...roles.map((role) => ({ value: role, label: role }))],
            },
            {
              label: "Verification",
              value: verifiedFilter,
              onChange: setVerifiedFilter,
              options: [{ value: "all", label: "All accounts" }, { value: "verified", label: "Verified" }, { value: "unverified", label: "Unverified" }],
            },
          ]}
          resultCount={totalItems}
          itemLabel="users"
        />
        {loading ? (
          <p className="ops-loading">Loading users…</p>
        ) : totalItems === 0 ? (
          <EmptyState icon={Users} title="No users found" description={users.length === 0 ? "Accounts will appear here as people register." : "Try adjusting your search or filters."} />
        ) : (
          <>
            <DataTable columns={columns} rows={pageItems} rowKey={(row) => row._id} />
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>

      <Modal
        open={createOpen}
        onClose={closeCreate}
        title="New User"
        size="md"
        footer={
          <>
            <button type="submit" form="create-user-form" className="ops-button ops-button--primary" disabled={creating}>{creating ? "Creating..." : "Create User"}</button>
            <button type="button" className="ops-button ops-button--secondary" onClick={closeCreate}>Cancel</button>
          </>
        }
      >
        <form id="create-user-form" onSubmit={submitCreate} className="ops-form">
          <div className="ops-form-section">
            <p className="ops-form-section-title">Profile</p>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">First name</label>
                <input required className="ops-input" value={createForm.firstName} onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label">Last name</label>
                <input required className="ops-input" value={createForm.lastName} onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })} />
              </div>
            </div>
            <div className="ops-form-group">
              <label className="ops-form-label">Email</label>
              <input required type="email" className="ops-input" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
            </div>
          </div>

          <div className="ops-form-section" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
            <p className="ops-form-section-title">Access</p>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Temporary password</label>
                <input required type="text" minLength={8} className="ops-input" placeholder="At least 8 characters" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} />
                <span className="ops-form-hint">Share this with the user directly — they can change it after signing in.</span>
              </div>
              <div>
                <label className="ops-form-label">Role</label>
                <select className="ops-select" value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}>
                  {roles.map((role) => <option key={role} value={role}>{role}</option>)}
                </select>
              </div>
            </div>
          </div>

        </form>
      </Modal>

      <Modal
        open={Boolean(editingUser)}
        onClose={closeEdit}
        title="Edit User"
        size="sm"
        footer={
          <>
            <button type="submit" form="edit-user-form" className="ops-button ops-button--primary" disabled={savingEdit}>{savingEdit ? "Saving..." : "Save Changes"}</button>
            <button type="button" className="ops-button ops-button--secondary" onClick={closeEdit}>Cancel</button>
          </>
        }
      >
        <form id="edit-user-form" onSubmit={submitEdit} className="ops-form">
          <div className="ops-form-section" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">First name</label>
                <input required className="ops-input" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label">Last name</label>
                <input required className="ops-input" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} />
              </div>
            </div>
            <div className="ops-form-group" style={{ marginBottom: 0 }}>
              <label className="ops-form-label">Email</label>
              <input required type="email" className="ops-input" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </div>
          </div>

        </form>
      </Modal>

      <Modal
        open={Boolean(passwordUser)}
        onClose={closePassword}
        title="Set Password"
        size="sm"
        footer={
          <>
            <button type="submit" form="set-password-form" className="ops-button ops-button--primary" disabled={savingPassword}>{savingPassword ? "Saving..." : "Set Password"}</button>
            <button type="button" className="ops-button ops-button--secondary" onClick={closePassword}>Cancel</button>
          </>
        }
      >
        <form id="set-password-form" onSubmit={submitPassword} className="ops-form">
          <div className="ops-form-section" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
            <p style={{ margin: "0 0 16px", color: "var(--ops-muted)", fontSize: 13 }}>
              Set a new password for <strong style={{ color: "var(--ops-ink)" }}>{passwordUser?.firstName} {passwordUser?.lastName}</strong>. This immediately signs them out of any existing session.
            </p>
            <div className="ops-form-group" style={{ marginBottom: 0 }}>
              <label className="ops-form-label">New password</label>
              <input required type="text" minLength={8} className="ops-input" placeholder="At least 8 characters" value={passwordValue} onChange={(e) => setPasswordValue(e.target.value)} />
            </div>
          </div>

        </form>
      </Modal>

      <Modal open={Boolean(viewingUser)} onClose={() => setViewingUser(null)} title="User Details" size="sm">
        {viewLoading ? (
          <p className="ops-loading">Loading...</p>
        ) : viewingUser && (
          <div className="ops-form">
            <div className="ops-form-section">
              <p className="ops-form-section-title">Profile</p>
              <p style={{ margin: "0 0 8px" }}><strong>Name:</strong> {viewingUser.firstName} {viewingUser.lastName}</p>
              <p style={{ margin: "0 0 8px" }}><strong>Email:</strong> {viewingUser.email}</p>
              <p style={{ margin: "0 0 8px" }}><strong>Role:</strong> <Badge tone={roleTone[viewingUser.role] || "neutral"}>{viewingUser.role}</Badge></p>
              <p style={{ margin: 0 }}><strong>Status:</strong> {viewingUser.verifiedAt ? <Badge tone="success">Verified</Badge> : <Badge tone="neutral">Unverified</Badge>}</p>
            </div>
            <div className="ops-form-section" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
              <p className="ops-form-section-title">Activity</p>
              <p style={{ margin: "0 0 8px" }}><strong>Joined:</strong> {new Date(viewingUser.createdAt).toLocaleDateString()}</p>
              {viewDetail && (
                <>
                  <p style={{ margin: "0 0 8px" }}><strong>Players:</strong> {viewDetail.playerCount}</p>
                  <p style={{ margin: 0 }}><strong>Enrollments:</strong> {viewDetail.enrollmentCount}</p>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
